import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Copy, Gift, Users, Sparkles, Share2 } from "lucide-react";

export default function Referrals() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/referral/me").then(({ data }) => setData(data)); }, []);

  const copy = () => {
    if (!data?.share_url) return;
    navigator.clipboard.writeText(data.share_url);
    toast.success("Link copied");
  };
  const nativeShare = async () => {
    if (!data?.share_url) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Career AI Hub — get hired faster",
          text: "Match Score is free. Try it with my link:",
          url: data.share_url,
        });
      } catch {}
    } else { copy(); }
  };

  if (!data) return <div className="p-20 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto" /></div>;

  return (
    <div className="max-w-4xl mx-auto px-5 md:px-8 py-10">
      <div className="mb-6">
        <div className="label-eyebrow">Refer a friend</div>
        <h1 className="text-4xl md:text-5xl font-black tracking-tighter">Give a hand, get a free month</h1>
        <p className="mt-2 text-gray-600 max-w-2xl">When a friend signs up with your link and upgrades to Pro, you get <strong>30 days of Pro free</strong>. Every time.</p>
      </div>

      <div className="nb-card p-8 bg-[#eab308]" data-testid="referral-card">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg border-2 border-black bg-white flex items-center justify-center">
            <Gift className="w-6 h-6" strokeWidth={2.5} />
          </div>
          <div>
            <div className="label-eyebrow">Your referral code</div>
            <div data-testid="referral-code" className="text-3xl font-black tracking-tighter">{data.code}</div>
          </div>
        </div>
        <div className="mt-6">
          <div className="label-eyebrow">Share this link</div>
          <div className="mt-2 flex flex-col sm:flex-row gap-2">
            <input data-testid="referral-url" readOnly value={data.share_url}
              className="flex-1 border-2 border-black rounded-lg px-3 py-2.5 bg-white font-mono text-sm truncate" />
            <button data-testid="referral-copy" onClick={copy} className="nb-btn bg-white px-4 py-2.5 inline-flex items-center gap-2"><Copy className="w-4 h-4" /> Copy</button>
            <button data-testid="referral-share" onClick={nativeShare} className="nb-btn bg-[#4338ca] text-white px-4 py-2.5 inline-flex items-center gap-2"><Share2 className="w-4 h-4" /> Share</button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6" data-testid="referral-stats">
        {[
          { icon: Users,     label: "Friends signed up", value: data.referred_count, bg: "bg-white" },
          { icon: Sparkles,  label: "Upgraded to Pro",   value: data.upgraded_count, bg: "bg-[#4338ca] text-white" },
          { icon: Gift,      label: "Free months earned", value: data.months_earned, bg: "bg-[#22c55e] text-white" },
        ].map((s, i) => (
          <div key={i} className={`nb-card p-5 ${s.bg}`}>
            <s.icon className="w-6 h-6" strokeWidth={2.5} />
            <div className="mt-2 text-4xl font-black">{s.value}</div>
            <div className="text-xs font-bold uppercase tracking-widest mt-1 opacity-90">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="nb-card p-6 mt-6">
        <h2 className="font-bold text-lg">How it works</h2>
        <ol className="mt-3 list-decimal ml-6 space-y-1.5 text-sm text-gray-800">
          <li>Share your link. Friends sign up with your referral code baked in.</li>
          <li>When they upgrade to Pro ($19), Career AI Hub instantly adds <strong>30 days of Pro</strong> to your account.</li>
          <li>Repeatable. Every friend = another free month.</li>
        </ol>
      </div>
    </div>
  );
}
